// src/components/admin/AIInsightsPanel.js

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  SparklesIcon, 
  ChartBarIcon, 
  ExclamationTriangleIcon,
  LightBulbIcon,
  ArrowTrendingUpIcon, // ✅ CORRECTED: Use ArrowTrendingUpIcon instead of TrendingUpIcon
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const AIInsightsPanel = ({ insights = [] }) => {
  const [selectedInsight, setSelectedInsight] = useState(null);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'from-red-500 to-pink-500';
      case 'medium': return 'from-yellow-500 to-orange-500';
      case 'low': return 'from-green-500 to-emerald-500';
      default: return 'from-blue-500 to-cyan-500';
    }
  };

  const getPriorityIcon = (type) => {
    switch (type) {
      case 'optimization': return <ArrowTrendingUpIcon className="w-5 h-5" />; // ✅ FIXED
      case 'engagement': return <ChartBarIcon className="w-5 h-5" />;
      case 'content': return <LightBulbIcon className="w-5 h-5" />;
      default: return <SparklesIcon className="w-5 h-5" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl shadow-xl p-6 border border-purple-100"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl">
          <SparklesIcon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">AI Insights & Recommendations</h2>
          <p className="text-gray-600">Powered by intelligent analysis of your data</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {insights.map((insight, index) => (
          <motion.div
            key={insight.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
            onClick={() => setSelectedInsight(insight)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 rounded-xl bg-gradient-to-r ${getPriorityColor(insight.priority)}`}>
                {getPriorityIcon(insight.type)}
                <div className="text-white text-xs mt-1 font-semibold">
                  {insight.priority?.toUpperCase()}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Confidence</div>
                <div className="text-lg font-bold text-gray-800">{insight.confidence}%</div>
              </div>
            </div>

            <h3 className="font-bold text-gray-800 text-lg mb-2">{insight.title}</h3>
            <p className="text-gray-600 text-sm mb-4 line-clamp-3">{insight.description}</p>
            
            <div className="flex items-center justify-between">
              <div className="text-green-600 font-semibold text-sm">{insight.impact}</div>
              <button className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg text-sm font-semibold hover:shadow-md transition-all">
                {insight.action}
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {insights.length === 0 && (
        <div className="text-center py-12">
          <SparklesIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-500 mb-2">No insights available</h3>
          <p className="text-gray-400">AI is analyzing your data to generate insights...</p>
        </div>
      )}

      {/* Insight Modal */}
      <AnimatePresence>
        {selectedInsight && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedInsight(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">{selectedInsight.title}</h2>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r ${getPriorityColor(selectedInsight.priority)} text-white text-sm font-semibold`}>
                    {getPriorityIcon(selectedInsight.type)}
                    {selectedInsight.priority?.toUpperCase()} PRIORITY
                  </div>
                </div>
                <button
                  onClick={() => setSelectedInsight(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              
              <p className="text-gray-700 text-lg mb-6">{selectedInsight.description}</p>
              
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 p-4 rounded-xl">
                  <h4 className="font-semibold text-green-800 mb-2">Expected Impact</h4>
                  <p className="text-green-600 text-xl font-bold">{selectedInsight.impact}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-xl">
                  <h4 className="font-semibold text-blue-800 mb-2">Confidence Level</h4>
                  <p className="text-blue-600 text-xl font-bold">{selectedInsight.confidence}%</p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <button className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all">
                  Implement Now
                </button>
                <button className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all">
                  Learn More
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AIInsightsPanel;
